import { createErrorApiResponse, getApiError } from "@entrilia/shared/api/axiosHelper";
import type { ApiResponse, BooleanOperationResult } from "@entrilia/shared/api/types";
import { defined } from "@entrilia/shared/utilities/typeHelper";
import type {
  ClientProfileApiResponse,
  DemoSourceTemplateCandidatesResponse,
  SetDemoSourceTemplateRequest,
} from "../../../api/clientsManagementTypes";
import { api } from "../../../api/operationsApi";
import type { ClientProfile, ToggleableClientProductKey } from "./clientProfilesTypes";

export interface UpdateClientProductStateRequest {
  clientCode: string;
  productKey: ToggleableClientProductKey;
  enabled: boolean;
}

const mapToClientProfile = (response: ClientProfileApiResponse): ClientProfile => ({
  id: response.clientId,
  title: response.clientName,
  clientCode: response.clientCode,
  type: response.clientType,
  companyType: response.companyType,
  sourceTemplateClientCode: response.sourceTemplateClientCode,
  entityLevelAccess: response.entityLevelAccessConfigured,
  managedByClients: response.managedByClients,
  managesClients: response.managesClients,
  applications: response.applications,
});

const mapResponseToClientProfiles = async (
  apiCall: Promise<ApiResponse<ClientProfileApiResponse[]>>
): Promise<ApiResponse<ClientProfile[]>> => {
  const response = await apiCall;
  if (!response.success) {
    return { success: false, error: response.error };
  }
  return { success: true, data: defined(response.data).map(mapToClientProfile) };
};

export interface UpdateExcelAddinUseBcReportGenerationViaSessionRequest {
  clientCode: string;
  enabled: boolean;
}

export const clientProfilesDataSource = {
  getClientProfiles: (): Promise<ApiResponse<ClientProfile[]>> => {
    return mapResponseToClientProfiles(api.clientProfiles.getClientProfiles());
  },
  getDemoSourceTemplateCandidates: (
    targetClientCode: string
  ): Promise<ApiResponse<DemoSourceTemplateCandidatesResponse>> => {
    return api.clientProfiles.getDemoSourceTemplateCandidates(targetClientCode);
  },
  setDemoSourceTemplate: (request: SetDemoSourceTemplateRequest): Promise<ApiResponse<BooleanOperationResult>> => {
    return api.clientProfiles.setDemoSourceTemplate(request);
  },
  updateClientProductState: async (request: UpdateClientProductStateRequest): Promise<ApiResponse<unknown>> => {
    try {
      const stateRequest = { clientCode: request.clientCode, enabled: request.enabled };
      switch (request.productKey) {
        case "investorPortal":
          return await api.clientProfiles.setInvestorPortalState(stateRequest);
        case "expenseManagement":
          return await api.clientProfiles.setExpenseManagementState(stateRequest);
        case "entriliaMcp":
          return await api.clientProfiles.setEntriliaMcpState(stateRequest);
        case "portfolioMonitoring":
          return await api.clientProfiles.setPortfolioMonitoringState(stateRequest);
        default: {
          const unknownKey: never = request.productKey;
          return createErrorApiResponse({ message: `Unknown product key: ${unknownKey}` });
        }
      }
    } catch (e) {
      return createErrorApiResponse(getApiError(e));
    }
  },
  updateExcelAddinUseBcReportGenerationViaSession: async (
    request: UpdateExcelAddinUseBcReportGenerationViaSessionRequest
  ): Promise<ApiResponse<unknown>> => {
    try {
      return await api.clientProfiles.setExcelAddinUseBcReportGenerationViaSession({
        clientCode: request.clientCode,
        enabled: request.enabled,
      });
    } catch (e) {
      return createErrorApiResponse(getApiError(e));
    }
  },
};
